import { useNavigate } from "@tanstack/react-router";
import { CalendarRange, Plus } from "lucide-react";
import { useId, useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useReviewConfig } from "@/hooks/use-review-config";
import { type ReviewCycle, useCreateReviewCycle, useReviewCycles } from "@/hooks/use-review-cycles";

/**
 * Review rounds — EPIC-053.
 *
 * "Round" on screen, `cycle` in the code and the API, matching the legacy app's
 * own table name so a migration of its rows reads straight across.
 */

const STATUS_TONES: Record<string, StatusTone> = {
  draft: "neutral",
  open: "success",
  closed: "info",
};

/** Dates are date-only strings; `new Date("2026-01-01")` would shift them. */
function formatDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function ReviewCyclesList() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError, error, refetch } = useReviewCycles();

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load review rounds"}
        onRetry={() => refetch()}
      />
    );
  }

  const cycles = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" aria-hidden />
          New round
        </Button>
      </div>

      <DataTable
        headers={[
          { label: "Round" },
          { label: "Status" },
          { label: "Period reviewed" },
          { label: "Due" },
          { label: "Progress" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={CalendarRange}
            title="No review rounds yet"
            description="A round sets the period being reviewed and when the reviewing happens. Create one, then generate everyone's reviews."
          />
        }
      >
        {cycles.map((cycle) => (
          <tr
            key={cycle.id}
            {...interactiveRowProps(
              () =>
                navigate({
                  to: "/dashboard/reviews/rounds/$cycleId",
                  params: { cycleId: cycle.id },
                }),
              `Open ${cycle.name}`,
            )}
          >
            <td className="px-4 py-3 font-medium">{cycle.name}</td>
            <td className="px-4 py-3">
              <StatusPill
                tone={STATUS_TONES[cycle.statusKey] ?? "neutral"}
                label={cycle.statusKey}
              />
            </td>
            <td className="px-4 py-3 text-muted-foreground text-sm">
              {formatDate(cycle.periodStart)} – {formatDate(cycle.periodEnd)}
            </td>
            <td className="px-4 py-3 text-muted-foreground text-sm">{formatDate(cycle.dueOn)}</td>
            <td className="px-4 py-3 text-sm tabular-nums">
              <CycleProgress cycle={cycle} />
            </td>
          </tr>
        ))}
      </DataTable>

      <CreateCycleDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

/** Counted live on every read, so it can never be a figure that has drifted. */
function CycleProgress({ cycle }: { cycle: ReviewCycle }) {
  if (cycle.reviewCount === 0) return <span className="text-muted-foreground">Not generated</span>;
  return (
    <span>
      {cycle.completeReviewCount} of {cycle.reviewCount} complete
    </span>
  );
}

function CreateCycleDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const create = useCreateReviewCycle();
  const nameId = useId();
  const periodStartId = useId();
  const periodEndId = useId();
  const opensOnId = useId();
  const dueOnId = useId();
  const cutoffId = useId();
  const overlapId = useId();
  const leadDaysId = useId();

  const [name, setName] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [opensOn, setOpensOn] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [cycleTypeKey, setCycleTypeKey] = useState("");
  const [excludeStartedAfter, setExcludeStartedAfter] = useState("");
  const [minimumOverlapHours, setMinimumOverlapHours] = useState("");
  const [conversationLeadDays, setConversationLeadDays] = useState("7");

  const { data: cycleTypes = [] } = useReviewConfig("cycle-types");
  const kinds = cycleTypes.filter((kind) => kind.isActive);
  const chosen = kinds.find((kind) => kind.key === cycleTypeKey);

  // The kind is what decides the roster, so it is required rather than a
  // detail to fill in later.
  const complete = name.trim() && cycleTypeKey && periodStart && periodEnd && opensOn && dueOn;

  const submit = async () => {
    const created = await create.mutateAsync({
      name: name.trim(),
      cycleTypeKey,
      periodStart,
      periodEnd,
      opensOn,
      dueOn,
      excludeStartedAfter: excludeStartedAfter || null,
      minimumOverlapDays: minimumOverlapHours ? Number(minimumOverlapHours) : null,
      managerConversationLeadDays: conversationLeadDays ? Number(conversationLeadDays) : null,
    });
    onOpenChange(false);
    setName("");
    navigate({ to: "/dashboard/reviews/rounds/$cycleId", params: { cycleId: created.id } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New review round</DialogTitle>
          <DialogDescription>
            The period being reviewed and the window for doing the reviewing are different things —
            a round covering the second half of the year is usually written up in January.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/*
            The kind of round, FIRST — above the name. It decides who the round
            covers, so it is the decision the rest of the dialog hangs off:
            whether the cut-off below is even offered, and what happens the
            moment this closes. Pills rather than a dropdown so all of the
            options, and the fact that there are only a few, are visible
            without opening anything (Matt, 2026-10-06).
          */}
          <fieldset className="space-y-2">
            <legend className="font-medium text-sm">Kind of round</legend>
            <div className="flex flex-wrap gap-2">
              {kinds.map((kind) => {
                const selected = kind.key === cycleTypeKey;
                return (
                  <button
                    key={kind.key}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setCycleTypeKey(kind.key)}
                    className={`rounded-full border px-3 py-1.5 font-medium text-sm transition-colors ${
                      selected
                        ? "border-primary bg-accent text-primary"
                        : "border-border hover:bg-muted/60"
                    }`}
                  >
                    {kind.name}
                  </button>
                );
              })}
            </div>
            {/*
              `min-h` so choosing a kind does not shunt the whole dialog down
              by a line the first time.
            */}
            <p className="min-h-5 text-muted-foreground text-sm">
              {chosen
                ? chosen.coversEveryone === false
                  ? "Starts empty — you name the people it covers."
                  : "Everyone eligible is pulled in automatically."
                : "Pick one — it decides who the round covers."}
            </p>
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              value={name}
              placeholder="2026 mid-year"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={periodStartId}>Period reviewed, from</Label>
              <Input
                id={periodStartId}
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={periodEndId}>to</Label>
              <Input
                id={periodEndId}
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={opensOnId}>Reviewing opens</Label>
              <Input
                id={opensOnId}
                type="date"
                value={opensOn}
                onChange={(e) => setOpensOn(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={dueOnId}>Responses due</Label>
              <Input
                id={dueOnId}
                type="date"
                value={dueOn}
                onChange={(e) => setDueOn(e.target.value)}
              />
            </div>
          </div>

          {/*
            WI-313. Only worth offering on a round that pulls everyone in — on
            one you build by hand there is nobody to leave out.
          */}
          {chosen?.coversEveryone !== false && (
            <div className="space-y-2">
              <Label htmlFor={cutoffId}>Leave out anyone who started after</Label>
              <Input
                id={cutoffId}
                type="date"
                value={excludeStartedAfter}
                onChange={(e) => setExcludeStartedAfter(e.target.value)}
              />
              <p className="text-muted-foreground text-sm">
                Optional. Someone hired since this date has usually just had a 90-day review, so
                they sit this round out. They are still listed, with the reason.
              </p>
            </div>
          )}

          {/*
            EPIC-056 WI-198. Peer derivation only runs for a round that sets
            this — left blank, nobody is automatically paired and peers can
            still be added by hand from a review's own page.
          */}
          <div className="space-y-2">
            <Label htmlFor={overlapId}>Minimum shared project hours for a peer</Label>
            <Input
              id={overlapId}
              type="number"
              min={0}
              step={1}
              value={minimumOverlapHours}
              onChange={(e) => setMinimumOverlapHours(e.target.value)}
              placeholder="e.g. 40"
            />
            <p className="text-muted-foreground text-sm">
              Optional. Two people are automatically paired as each other's peer reviewer once each
              has logged at least this many hours on a shared project during the period reviewed.
              Left blank, nobody is paired automatically — peers can still be added by hand.
            </p>
          </div>

          {/* EPIC-081 WI-303. Measured back from the due date; blank turns it off. */}
          <div className="space-y-2">
            <Label htmlFor={leadDaysId}>
              Remind managers about conversations this many days early
            </Label>
            <Input
              id={leadDaysId}
              type="number"
              min={0}
              max={90}
              step={1}
              value={conversationLeadDays}
              onChange={(e) => setConversationLeadDays(e.target.value)}
              placeholder="e.g. 7"
            />
            <p className="text-muted-foreground text-sm">
              Managers with direct reports they haven't signed off yet get one email this many days
              before the responses are due. Left blank, no reminder is sent.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!complete || create.isPending}>
            Create round
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
